import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import api from "../../../shared/api/axios";
import { useSocket } from "../../../shared/socket/SocketProvider";
import { useAuth } from "../../../shared/contexts/AuthContext";
import SidebarChat from "../../sidebars/SidebarChat";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import EmojiPickerChat from "../../../shared/chat/EmojiPickerChat";
import MessageOutlook, { ui as messageUi } from "./MessageOutlook";
import { flushSync } from "react-dom";
import { encryptMessage, decryptMessage, publicKeyFromSecret } from "../../../shared/crypto/simpleE2EE";
import { computeAutoPosition } from "../../../shared/chat/positioning";
import type { Message, User, ChatLocationState, ReplyDraft, UserStatus } from "../../../shared/chat/chatTypes";
import { idOf } from "../../../shared/chat/idOf";
import { requireRecipientKey, requireMySecret } from "../../../shared/chat/chatAsync";
import { makeChatPageUi } from "./chatPageUi";
import { applyAlbumManifestInPlace } from "../../../shared/chat/chatExpiry";
import { normalizeMessage, parseAlbumIds, deriveType, byCreated } from "../../../shared/chat/chatMessageUtils";
import { nextActiveChatsAfterIncoming } from "../../../shared/chat/chatActiveChats";
import ProfileActions from "../../../shared/chat/ProfileActions";
import { useMediaAdditionsChat } from "./MediaAdditionsChat";
import { useTranslation } from 'react-i18next';
import { useActiveChats } from "../../../shared/contexts/ActiveChatsContext";

const SIDEBAR_WIDTH = 360;   // must match SidebarChat's width
const SIDEBAR_GUTTER = 20;   // little breathing room
const pageUi = makeChatPageUi({ SIDEBAR_WIDTH, SIDEBAR_GUTTER });

// Chat
const Chat: React.FC = () => { 
  const { user, e2eeReady } = useAuth();
  const { socket, connected } = useSocket();
  const [search, setSearch] = useState<string>("");
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [message, setMessage] = useState<string>("");
  const { activeChats, setActiveChats } = useActiveChats();
  const [hoverMsgId, setHoverMsgId] = useState<string | null>(null);
  const [msgMenuOpenId, setMsgMenuOpenId] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const location = useLocation();
  const { userId: routeUserId } = useParams<{ userId?: string }>();
  const [emojiOpen, setEmojiOpen] = useState(false);
  const replyLookupRef = useRef<Record<string, Message>>({});

  const [replyTo, setReplyTo] = useState<ReplyDraft | null>(null);
  const replyPreviewRef = React.useRef<Record<string, { author: string; text: string }>>({});
  const [replyLookupTick, setReplyLookupTick] = React.useState(0);

  const emojiBtnRef = useRef<HTMLButtonElement | null>(null);
  const sendingRef = React.useRef(false);
  
  const [deleteTarget, setDeleteTarget] = useState<{
  messageId: string;
  mine: boolean;
} | null>(null);

const [deleteBusy, setDeleteBusy] = useState(false);

  const {
    toUserId: stateToUserId,
    toUserName,
    toUserPicture,
  } = (location.state || {}) as ChatLocationState & { justSentIds?: string[] };

  const toUserId = stateToUserId || routeUserId; // 👈 prefer state, fallback to route

  const [msgMenuStyle, setMsgMenuStyle] = useState<React.CSSProperties | null>(null);
  const navigate = useNavigate();
  const picUrl = (p?: string) =>
    p?.startsWith("/uploads") ? `http://localhost:3001${p}` : p || "";

const messagesContainerRef = useRef<HTMLDivElement | null>(null);

const { showMediaForMessage } = useMediaAdditionsChat({
  selectedUserId: selectedUser?._id || null,
  myUser: user,
  navigate,
  location,
  scrubState: { toUserId, toUserName, toUserPicture },
});

// simple bundleKey generator
const makeBundleKey = () =>
  crypto.randomUUID?.() ||
  `bundle-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Pagenation
type Cursor = { t: string; id: string } | null;
const topSentinelRef = useRef<HTMLDivElement | null>(null);
const suppressStickRef = useRef(false);
const nextFrame = () => new Promise<void>(r => requestAnimationFrame(() => r()));
const nextPaint = async () => { await nextFrame(); await nextFrame(); };
const cursorRef = React.useRef<Cursor>(null);
const [hasMore, setHasMore] = React.useState(false);
const [isLoadingOlder, setIsLoadingOlder] = React.useState(false);


//Socket
useEffect(() => {
  if (!socket || !connected) return;
  let cancelled = false;

  const onIncomingMessage = async (raw: any) => {
    // normalize + (optionally) hydrate users if your UI depends on it
    const msg = normalizeMessage(raw);

    // 1) insert immediately
    setMessages(prev => {
      const exists = prev.some(m => String(m._id) === String(msg._id));
      return exists ? prev : [...prev, msg].sort(byCreated);
    });

    // 2) decrypt if needed
    try {
      if (!msg.isEncrypted) return;
      if (!e2eeReady) return;

      const me = myIdRef.current || idOf(user);
      const iAmSender = String(idOf(msg.sender)) === String(me);

      const envelope = iAmSender ? (msg as any).selfEnvelope : (msg as any).encrypted;
      if (!envelope) return;

      const mySecret = await requireMySecret();
      const decryptedText = await decryptMessage(envelope, mySecret); // ✅ IMPORTANT

      if (cancelled) return;

      // 3) patch decrypted text into message list
      setMessages(prev =>
        prev.map(m =>
          String(m._id) === String(msg._id)
            ? { ...m, decryptedText, text: decryptedText }
            : m
        )
      );

      // 4) update sidebar preview with decrypted text
      const isOpenThread =
        openThreadIdRef.current &&
        String(openThreadIdRef.current) ===
          String(iAmSender ? idOf(msg.recipient) : idOf(msg.sender));

      setActiveChats(prev =>
        nextActiveChatsAfterIncoming(
          prev,
          { ...msg, decryptedText, text: decryptedText } as any,
          me,
          !!isOpenThread,
          {
            message: "Message",
            photo: "Photo",
            video: "Video",
            audio: "Audio",
            media: "Media",
            fallbackUser: "User",
          }
        )
      );

      setActiveChats(prev =>
  prev.map(c =>
    c._id === String(
      String(idOf(msg.sender)) === String(me)
        ? idOf(msg.recipient)
        : idOf(msg.sender)
    )
      ? {
          ...c,
          lastMessage: decryptedText,
          lastMessageType: "text",
        }
      : c
  )
);

    } catch (e) {
      console.warn("socket decrypt failed", e);
    }
  };

  socket.on("message", onIncomingMessage);
  return () => {
    cancelled = true;
    socket.off("message", onIncomingMessage);
  };
}, [socket, connected, user, e2eeReady]);


useEffect(() => {
  if (!e2eeReady) return;

  (async () => {
    const me = myIdRef.current || idOf(user);
    const mySecret = await requireMySecret();

    setMessages(prev => prev); // no-op; we’ll set real value below

    const next = await Promise.all(
      messagesRef.current.map(async (m: any) => {
        if (!m?.isEncrypted) return m;
        if (m.decryptedText) return m;

        const iAmSender = String(idOf(m.sender)) === String(me);
        const envelope = iAmSender ? m.selfEnvelope : m.encrypted;
        if (!envelope) return m;

        try {
          const decryptedText = await decryptMessage(envelope, mySecret);
          return { ...m, decryptedText, text: decryptedText };
        } catch {
          return m;
        }
      })
    );

    setMessages(next);
  })();
}, [e2eeReady, user]);


// ===== helpers at top-level in Chat component =====
const expiredRef = useRef<{ parentId?: string; ids: string[] } | null>(null);
const messagesRef = useRef<Message[]>([]);
const markExpiredNow = React.useCallback((ids: string[], parentId?: string) => {
  setMessages(prev =>
    prev.map(m => {
      const id = String(m._id);

      // also expire album parents if any child id is in ids
      const album = ((m as any).albumIds ?? parseAlbumIds(m)) || [];
      const intersects = Array.isArray(album) && album.some((x: string) => ids.includes(String(x)));

      if (ids.includes(id) || intersects || (parentId && id === parentId)) {
        return { ...m, isExpiredMedia: true } as Message;
      }
      return m;
    })
  );
}, []);

///////////////
//Temporary Debug
///////////////
function E2EEDebug() {
  const { e2eeReady, e2eeNeedsPassword } = useAuth();
  useEffect(() => {
    console.log('[WEB] e2eeReady:', e2eeReady,
      'e2eeNeedsPassword:', e2eeNeedsPassword,
      'hasLocalSecret:', !!localStorage.getItem('my:secretKey'));
  }, [e2eeReady, e2eeNeedsPassword]);
  return null;
}

//Helper for User/ProfileName
const hydrateUsers = (m: any, participants: Record<string, any>) => {
  const pick = (v: any) => (typeof v === "string" ? v : v?._id || v?.id || "");
  const asUser = (id: string) => {
    const p = participants?.[id];
    return {
      _id: id,
      profilename: p?.profilename || p?.name || "User",
      picture: typeof p?.picture === "string" ? p.picture : "",
    };
  };

  const sId = pick(m.sender);
  const rId = pick(m.recipient);

  if (sId) m.sender = asUser(sId);
  if (rId) m.recipient = asUser(rId);

  return m;
};


//Loading of older messages
const loadOlder = async () => {
  if (!selectedUser || !hasMore || isLoadingOlder) return;
  setIsLoadingOlder(true);
  suppressStickRef.current = true; // block auto-sticking while we prepend
  userAtBottomRef.current = false;

  const el = messagesContainerRef.current;
  const prev = el ? { top: el.scrollTop, height: el.scrollHeight } : null;

  let ro: ResizeObserver | null = null;
if (el && prev && "ResizeObserver" in window) {
  (el.style as any).overflowAnchor = "none";
  ro = new ResizeObserver(() => {
    const delta = el.scrollHeight - prev.height;
    el.scrollTop = prev.top + delta;
  });
  ro.observe(el);
}
  try {
    await fetchMessages(selectedUser._id, { initial: false, limit: 20 });
  } finally {
    setIsLoadingOlder(false);

   // final correction and cleanup
   if (el && prev) {
     const delta = el.scrollHeight - prev.height;
     el.scrollTop = prev.top + delta;
   }
   if (ro) ro.disconnect();
   if (el) (el.style as any).overflowAnchor = '';
    suppressStickRef.current = false;
  }
};

useEffect(() => {
  const root = messagesContainerRef.current;
  const sent = topSentinelRef.current;
  if (!root || !sent) return;

  const obs = new IntersectionObserver(
    (entries) => {
      const hit = entries.some(e => e.isIntersecting);
      if (!hit) return;
      // guard: don't spam if already loading or no more
      if (isLoadingOlder || !hasMore) return;
      loadOlder(); // fire and forget
    },
    {
      root,
      rootMargin: '400px 0px 0px 0px', // start loading before we reach the top
      threshold: 0,
    }
  );

  obs.observe(sent);
  return () => obs.disconnect();
}, [hasMore, isLoadingOlder, selectedUser?._id]);

// track whether user is near the bottom
const userAtBottomRef = useRef(true);
// force-scroll when changing selectedUser
// during this window, we'll force-snap on media loads regardless of userAtBottomRef
const snapNow = () => {
  const el = messagesContainerRef.current;
  if (!el) return;
  hardJumpToBottom(el);
};

useEffect(() => { messagesRef.current = messages; }, [messages]);

useEffect(() => {
  return () => {
    // when leaving the chat page entirely
    setReplyTo(null);
    setMessage("");
  };
}, []);


const isNearBottom = (el: HTMLElement, px = 96) => {
  const diff = el.scrollHeight - (el.scrollTop + el.clientHeight);
  return diff <= px; // within 96px of bottom
};

useEffect(() => {
  const el = messagesContainerRef.current;
  if (!el) return;
  const onScroll = () => { userAtBottomRef.current = isNearBottom(el); };
  userAtBottomRef.current = isNearBottom(el);
  el.addEventListener("scroll", onScroll, { passive: true });
  return () => el.removeEventListener("scroll", onScroll);
}, []);

const actionRef = useRef<null | 'open' | 'send'>(null); // what just happened
const prevLenRef = useRef<number>(0);                   // previous messages length

function hardJumpToBottom(el: HTMLElement) {
  const prevSB = el.style.scrollBehavior;
  const prevOA = (el.style as any).overflowAnchor;

  el.style.scrollBehavior = "auto";        // never smooth
  (el.style as any).overflowAnchor = "none";
  el.scrollTop = el.scrollHeight;          // hard set
  el.style.scrollBehavior = prevSB || "";
  (el.style as any).overflowAnchor = prevOA || "";
  userAtBottomRef.current = true;
}
const openThreadIdRef = useRef<string | null>(null);
useEffect(() => {
  openThreadIdRef.current = selectedUser?._id ?? null;
}, [selectedUser?._id]);

// Handlers for Message Menu
const handleDeleteMessage = (messageId: string, mine: boolean) => {
  setDeleteTarget({ messageId, mine });
};

const confirmDeleteMessage = async () => {
  if (!deleteTarget) return;

  const { messageId } = deleteTarget;

  try {
    setDeleteBusy(true);

    await api.delete(`/message/${messageId}`).catch(() => {
      console.warn("Delete API failed, removing locally anyway");
    });

    setMessages(prev =>
      prev.filter(m => m._id !== messageId)
    );

  } finally {
    setDeleteBusy(false);
    setDeleteTarget(null);
    setMsgMenuOpenId(null);
  }
};

async function getUserStatus(userId: string): Promise<UserStatus> {
  try {
    const { data } = await api.get(`/users/public/${encodeURIComponent(userId)}/status`);
    return {
      isDeleted: !!data?.isDeleted,
      profilename: typeof data?.profilename === 'string' ? data.profilename : null,
    };
  } catch (err: any) {
    // If the user was hard-deleted, the endpoint will likely 404 → treat as deleted.
    const status = err?.response?.status;
    if (status === 404) {
      return { isDeleted: true, profilename: t('chat.deletedUser') };
    }
    // Any other error: don’t guess; defer to UI hints later
    return { isDeleted: false, profilename: null };
  }
}

// Delete an entire conversation with a user
const handleDeleteChat = React.useCallback(async (chatUserId: string) => {
  // Ask backend first
  let { isDeleted: otherIsDeleted, profilename } = await getUserStatus(chatUserId);

  // Local fallback name & deleted hint if API didn’t help
  const activeRow = activeChats.find(c => c._id === chatUserId);
  const localName =
    profilename ??
    activeRow?.profilename ??
    (selectedUser && selectedUser._id === chatUserId ? selectedUser.profilename : null) ??
    null;

  // If API didn’t say deleted, infer from UI label
  if (!otherIsDeleted) {
    const looksDeleted =
      (localName && localName.trim().toLowerCase() === 'deleted user') ||
      (activeRow?.profilename?.trim().toLowerCase() === 'deleted user');
    if (looksDeleted) otherIsDeleted = true;
  }

  const confirmText = otherIsDeleted
  ? t('chat.deleteConversation.deletedUser')
  : t('chat.deleteConversation.normal', { name: localName || '' });

  if (!window.confirm(confirmText)) return;

  try {
    await api.delete(
      `/message/conversations/${encodeURIComponent(chatUserId)}${
        otherIsDeleted ? '?purgeIfOtherDeleted=1' : ''
      }`
    );
  } catch (err) {
    console.warn('Delete conversation failed (clearing local state anyway):', err);
  }
  // Clear UI
  setMessages([]);
  setActiveChats(prev => prev.filter(u => u._id !== chatUserId));
  setSelectedUser(prev => (prev?._id === chatUserId ? null : prev));
  setMsgMenuOpenId(null);
}, [activeChats, selectedUser]);

// Copy plaintext (already decrypted by the time this is called)
const handleCopyMessage = React.useCallback(async (text: string) => {
  const val = text ?? "";

  try {
    await navigator.clipboard.writeText(val);
  } catch {
    // Fallback for older browsers / denied permissions
    const ta = document.createElement("textarea");
    ta.value = val;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
  }

  setMsgMenuOpenId(null);
}, []);


// Keep the most recent expired payload so we can apply it to future fetches/socket pushes

const applyExpired = React.useCallback((arr: Message[]) => {
  const ex = expiredRef.current;
  if (!ex) return arr;

  const ids = new Set((ex.ids || []).map(String));
  const parentId = ex.parentId ? String(ex.parentId) : null;

  return arr.map((m) => {
    const id = String(m._id);
    // consider album parents expired if ANY child id is expired
    const album = ((m as any).albumIds ?? parseAlbumIds(m)) || [];
    const albumHit = Array.isArray(album) && album.some((x: string) => ids.has(String(x)));

    if (ids.has(id) || albumHit || (parentId && id === parentId)) {
      return (m as any).isExpiredMedia ? m : ({ ...m, isExpiredMedia: true } as Message);
    }
    return m;
  });
}, []);

// Start a reply to a specific message (prefers decrypted text)

const activeReply = replyTo && selectedUser && replyTo.threadId === selectedUser._id ? replyTo : null;

const handleReplyToMessage = React.useCallback((m: Message) => {
  const threadId = selectedUser?._id;
  if (!threadId) return;

  setReplyTo({
    id: m._id,
    text: (m.decryptedText ?? m.text ?? "").trim(),
    threadId,
    authorName:
      (m.sender as any)?.profilename ||
      (m.sender as any)?.username ||
      "User",
  });

  setMsgMenuOpenId(null);
  setTimeout(() => inputRef.current?.focus(), 0);
}, [selectedUser?._id]);
function decorateReplyPreview(list: Message[]) {
  return list.map((m) => {
    if (!m.replyToId) return m;

    // ✅ if already set, do NOT touch (prevents flicker)
    const alreadyAuthor = (m as any).replyPreviewAuthor;
    const alreadyText   = (m as any).replyPreviewText;
    if (alreadyAuthor && alreadyText) return m;

    const rid = String(m.replyToId);

    // 1) Prefer cached preview (most stable)
    const cached = replyPreviewRef.current[rid];
    if (cached?.author || cached?.text) {
      return {
        ...m,
        replyPreviewAuthor: alreadyAuthor ?? cached.author ?? "",
        replyPreviewText:   alreadyText   ?? cached.text   ?? "",
      };
    }

    // 2) Fallback to lookup ref
    const rm = replyLookupRef.current[rid];
    if (!rm) return m;

    const author =
      (rm.sender as any)?.profilename ||
      (rm.sender as any)?.username ||
      "User";

    const txt =
      (rm as any).decryptedText ||
      (rm as any).text ||
      "";

    const cleaned = String(txt).trim();
    if (!cleaned) return m;

    // ✅ write into cache for later renders
    replyPreviewRef.current[rid] = { author, text: cleaned.slice(0, 160) };

    return {
      ...m,
      replyPreviewAuthor: alreadyAuthor ?? author,
      replyPreviewText:   alreadyText   ?? cleaned.slice(0, 160),
    };
  });
}




useEffect(() => {
  // if you’re not on a chat path anymore, clear composer UI state
  const isOnChatPage = location.pathname.startsWith("/chat");
  if (!isOnChatPage) {
    setReplyTo(null);
    setEmojiOpen(false);
    setMsgMenuOpenId(null);
    setMessage("");
  }
}, [location.pathname]);

const replyDraftsRef = React.useRef<Record<string, ReplyDraft | null>>({});


const replyToRef = React.useRef<ReplyDraft | null>(null);
React.useEffect(() => {
  replyToRef.current = replyTo;
}, [replyTo]);

// If you do NOT want that behavior, you can delete draftsRef entirely.
const draftsRef = React.useRef<Record<string, string>>({});
const lastChatIdRef = React.useRef<string | null>(null);
const messageRef = React.useRef<string>("");
React.useEffect(() => { messageRef.current = message; }, [message]);

// keep the latest message value so chat-switch effect can save it
useEffect(() => {
  messageRef.current = message;
}, [message]);
useEffect(() => {
  const chatId = selectedUser?._id ? String(selectedUser._id) : null;
  const prevId = lastChatIdRef.current;

  // leaving chat view / no active chat
  if (!chatId) {
    if (prevId) {
      const prevIdStr = String(prevId);
      draftsRef.current[prevIdStr] = messageRef.current;

      replyDraftsRef.current[prevIdStr] =
        replyToRef.current && replyToRef.current.threadId === prevIdStr
          ? replyToRef.current
          : null;
    }

    setMessage("");
    setReplyTo(null);
    setEmojiOpen(false);
    setMsgMenuOpenId(null);
    lastChatIdRef.current = null;
    return;
  }

  // first open
  if (!prevId) {
    lastChatIdRef.current = chatId;
    setMessage(draftsRef.current[chatId] ?? "");
    setReplyTo(replyDraftsRef.current[chatId] ?? null);
    return;
  }

  // switching chats
  if (prevId !== chatId) {
    const prevIdStr = String(prevId);

    // save previous chat state
    draftsRef.current[prevIdStr] = messageRef.current;
    replyDraftsRef.current[prevIdStr] =
      replyToRef.current && replyToRef.current.threadId === prevIdStr
        ? replyToRef.current
        : null;

    // restore next chat state
    lastChatIdRef.current = chatId;
    setMessage(draftsRef.current[chatId] ?? "");
    setReplyTo(replyDraftsRef.current[chatId] ?? null);

    setEmojiOpen(false);
    setMsgMenuOpenId(null);
  }
}, [selectedUser?._id]);


// Lookup a message in the current thread
const findMessageById = (id?: string) => {
  if (!id) return undefined;
  const s = String(id);
  return messagesRef.current.find(m => String(m._id) === s) || replyLookupRef.current[s];
};


const isMine = (m: any): boolean => {
  const me = myIdRef.current || idOf(user);
  return idOf(m.sender) === me || idOf(m.senderId) === me;
};
// --- Reply preview helpers (stable + hoisted) ---

function getProfileName(u: any): string {
  // prefer the same field you use elsewhere in UI
  return String(u?.profilename || u?.name || u?.username || "").trim();
}

function isAlbumManifestText(s: string): boolean {
  const t = String(s || "").trim();
  if (!t) return false;

  // common pattern: manifest is JSON with albumIds or type marker
  if (t.startsWith("{") || t.startsWith("[")) {
    try {
      const j = JSON.parse(t);
      if (Array.isArray(j?.albumIds) && j.albumIds.length > 0) return true;
      if (j?.type === "album-manifest" || j?.kind === "albumManifest") return true;
    } catch {
      // ignore JSON parse errors
    }
  }
  return false;
}

function getBestMessageText(m: any): string {
  const raw = (m?.decryptedText ?? m?.text ?? "").toString();
  const s = raw.trim();
  if (!s) return "";
  if (isAlbumManifestText(s)) return "";
  return s;
}

function cacheReplyPreview(
  m: any,
  replyPreviewRef: React.MutableRefObject<Record<string, { author: string; text: string }>>,
  setReplyLookupTick: React.Dispatch<React.SetStateAction<number>>
) {
  if (!m?._id) return;

  const author = getProfileName(m.sender);
  const text = getBestMessageText(m);

  if (!author && !text) return;

  const prev = replyPreviewRef.current[String(m._id)];
  const shouldUpdate =
    !prev ||
    (!!author && prev.author !== author) ||
    (!!text && prev.text !== text);

  if (shouldUpdate) {
    replyPreviewRef.current[String(m._id)] = {
      author: author || prev?.author || "",
      text: text || prev?.text || "",
    };
    setReplyLookupTick((x) => x + 1);
  }
}


/** Fetch messages for selected user */
const fetchMessages = async (
  userId: string,
  opts?: { initial?: boolean; limit?: number }
) => {
  try {
    const initial = opts?.initial ?? false;
    const limit = opts?.limit ?? 20;

    const params: Record<string, any> = { limit };
    if (!initial && cursorRef.current) {
      params.beforeT = cursorRef.current.t;
      params.beforeId = cursorRef.current.id;
    }

    const res = await api.get(`/message/${userId}`, { params });

    const data = res.data || {};
    const items: any[] = Array.isArray(data.items) ? data.items : [];
    const participants = data.participants || {};
    const replyLookup = data.replyLookup || {};
    const nextCursor = data.nextCursor || null;
    const more = !!data.hasMore;

    // 1) hydrate + normalize replyLookup first (so previews can be stable immediately)
    for (const raw of Object.values(replyLookup)) {
      const nm = normalizeMessage(raw as any);
      hydrateUsers(nm as any, participants);
      applyAlbumManifestInPlace(nm as any);

      replyLookupRef.current[String((nm as any)._id)] = nm as any;

      cacheReplyPreview(nm as any, replyPreviewRef, setReplyLookupTick);
    }

    // 2) Normalize + hydrate message list
    const list: Message[] = items.map((raw) => {
      const nm = normalizeMessage(raw);
      hydrateUsers(nm as any, participants);
      applyAlbumManifestInPlace(nm as any);
      return nm;
    });

    // 3) Decrypt + cache previews
    for (const m of list) {
      const me = myIdRef.current || idOf(user);
      const amRecipient =
        idOf((m as any).recipient) === me ||
        idOf((m as any).recipient?._id) === me ||
        idOf((m as any).receiverId) === me;

      if (m.isEncrypted) {
        const hasTextEnv = !!(m as any).encrypted || !!(m as any).selfEnvelope;

        if (hasTextEnv) {
          try {
            const mySecret = await requireMySecret();

            if (amRecipient && (m as any).encrypted) {
              (m as any).decryptedText = await decryptMessage((m as any).encrypted, mySecret);
            } else if (isMine(m) && (m as any).selfEnvelope) {
              (m as any).decryptedText = await decryptMessage((m as any).selfEnvelope, mySecret);
            } else if (!(m as any).decryptedText) {
              (m as any).decryptedText = "(encrypted)";
            }
          } catch {
            (m as any).decryptedText = "(failed to decrypt)";
          }
        } else {
          (m as any).decryptedText = undefined; // media-only
        }
      }

      cacheReplyPreview(m as any, replyPreviewRef, setReplyLookupTick);
    }

    // 4) Merge + sort + fill reply preview fields (stable)
    setMessages((prev) => {
      const map = new Map<string, Message>(prev.map((p) => [String((p as any)._id), p]));

      for (const m of list) {
        const key = String((m as any)._id);
        const existing = map.get(key);

        map.set(
          key,
          existing
            ? {
                ...existing,
                ...m,
                decryptedText: (m as any).decryptedText ?? (existing as any).decryptedText,
              }
            : m
        );
      }

      let out = Array.from(map.values()).sort(byCreated);
      out = decorateReplyPreview(out); // uses replyPreviewRef + replyLookupRef
      out = applyExpired(out);
      return out;
    });

    // 5) Update cursor + hasMore
    cursorRef.current =
      nextCursor && nextCursor.t && nextCursor.id
        ? { t: String(nextCursor.t), id: String(nextCursor.id) }
        : null;

    setHasMore(more);
    setReplyLookupTick((t) => t + 1); // ensure reply previews refresh
  } catch (err) {
    console.error("Error fetching messages:", err);
  }
};



// All media messages that belong to any album should be hidden in the list.
const hiddenChildIds = React.useMemo(() => {
  const set = new Set<string>();
  for (const m of messages) {
    const ids = (m as any).albumIds ?? parseAlbumIds(m);
    if (Array.isArray(ids)) {
      ids.forEach((id: string) => set.add(id));
    }
  }
  return set;
}, [messages]);

// Final list you actually render in the chat (albums stay, children hidden)
const displayMessages = React.useMemo(
  () => messages.filter(m => !hiddenChildIds.has(m._id)),
  [messages, hiddenChildIds]
);

// --- sticky bottom window (no visible scrolling) ---
const stickRafRef = useRef<number | null>(null);
const stickUntilRef = useRef(0);

const stopStick = () => {
  if (stickRafRef.current != null) {
    cancelAnimationFrame(stickRafRef.current);
    stickRafRef.current = null;
  }
};

const startStick = () => {
  if (stickRafRef.current != null) return; // already sticking
  const step = () => {
    const el = messagesContainerRef.current;
    if (!el) { stopStick(); return; }

    const now = performance.now();
    if (now >= stickUntilRef.current || !userAtBottomRef.current) {
      stopStick();
      return;
    }

    // instant jump (no smooth)
    const prevSB = el.style.scrollBehavior;
    el.style.scrollBehavior = "auto";
    el.scrollTop = el.scrollHeight; // keep pinned before paint
    el.style.scrollBehavior = prevSB || "";

    stickRafRef.current = requestAnimationFrame(step);
  };
  stickRafRef.current = requestAnimationFrame(step);
};

const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

const autoGrow = () => {
  const el = textareaRef.current;
  if (!el) return;
  el.style.height = "0px";                  // reset
  el.style.height = `${el.scrollHeight}px`; // grow to content
};

const stickFor = (ms = 1000) => {
  const now = performance.now();
  stickUntilRef.current = Math.max(stickUntilRef.current, now + ms);
  startStick();
};

//Read message
const markMessagesAsRead = async (userId: string) => {
  try {
    await api.post(`/message/mark-as-read/${userId}`, {});
      } catch (err) {
      console.error("Error marking messages as read:", err);
  }
};

/** Handle message sending (text) */
const handleSend = async () => {
  if (sendingRef.current) return;         // ✅ prevent double send
  sendingRef.current = true;

  try {
    if (!selectedUser || !user) return;
    if (!e2eeReady) {
      alert(t('chat.e2eeInitializing'));
      return;
    }

    const text = message.trim();
    if (!text) return;

    actionRef.current = "send";

    const tmpId = `tmp-${Date.now()}`;
    const optimistic: Message = {
      _id: tmpId,
      sender: { _id: myIdRef.current || idOf(user), profilename: user.profilename, picture: user.picture },
      recipient: { _id: selectedUser._id, profilename: selectedUser.profilename, picture: selectedUser.picture },
      text: "",
      createdAt: new Date().toISOString(),
      replyToId: replyTo?.id,
      isEncrypted: true,
      decryptedText: text,
      replyPreviewAuthor: replyTo?.authorName, 
      replyPreviewText: replyTo?.text,
    };

    flushSync(() => setMessages(prev => [...prev, optimistic].sort(byCreated)));
    stickFor(1200);
    snapNow();

    const mySecret = await requireMySecret();
    const myPub    = publicKeyFromSecret(mySecret);
    const recipientPub = await requireRecipientKey(selectedUser._id);

    const encrypted    = encryptMessage(text, recipientPub, mySecret);
    const selfEnvelope = encryptMessage(text, myPub,        mySecret);

    const bundleKey = makeBundleKey();

    const res = await api.post("/message/send", {
      receiverId: selectedUser._id,
      encrypted,
      selfEnvelope,
      bundleKey,
      ...(replyTo?.id ? { replyToId: replyTo.id } : {})
    });

    const serverMsg = normalizeMessage(res.data || {});
    serverMsg.decryptedText = serverMsg.decryptedText ?? optimistic.decryptedText;

    setMessages(prev => {
      const filtered = prev.filter(m => m._id !== tmpId && m._id !== serverMsg._id);
      return [...filtered, serverMsg].sort(byCreated);
    });

    setMessage("");
    setReplyTo(null);

    const last = (serverMsg.decryptedText || serverMsg.text || "Message").trim();
    setActiveChats(prev =>
      prev.some(c => c._id === selectedUser._id)
        ? prev.map(c =>
            c._id === selectedUser._id
              ? { ...c, lastMessage: last, lastMessageType: "text" }
              : c
          )
        : [
            {
              _id: selectedUser._id,
              profilename: selectedUser.profilename,
              picture: selectedUser.picture,
              lastMessage: last,
              lastMessageType: "text",
              unread: 0,
            },
            ...prev,
          ]
    );
  } catch (err: any) {
    // rollback optimistic
    // (only works if tmpId is in scope; if you want, move tmpId outside try)
    console.error("Error sending message:", err?.message || err);
    const apiMsg = err?.response?.data?.msg || err?.response?.data?.error;
    if (apiMsg) alert(t('chat.sendFailed', { msg: apiMsg }));
  } finally {
    sendingRef.current = false;           // ✅ always release lock
  }
};

useEffect(() => {
  if (!e2eeReady || !toUserId) return;

  (async () => {
    actionRef.current = "open";

    // start clean so the browser doesn’t anchor to old content
    setMessages([]);
    userAtBottomRef.current = true;

    // start from navigation state, but let backend override
    let name: string | null = toUserName || null;
    let picture: string = toUserPicture || "";

    try {
      const res = await api.get(`/users/${toUserId}`);
      const data = res.data || {};

      // backend profilename wins, but keep fallback if empty
      name = data.profilename || name || "User";

      // 🔥 IMPORTANT: trust backend picture, even if it's an empty string
      if (typeof data.picture === "string") {
        picture = data.picture; // '' when blocked → show 👻
      }
    } catch (err) {
      console.error("Error hydrating chat header user:", err);
      // if it fails, we just keep whatever name/picture we already had
    }

    const target: User = {
      _id: toUserId,
      profilename: name || "User",
      picture, // do NOT coalesce to "|| ''" here, we want to keep backend's ''
    };

    setSelectedUser(target);

    // ensure activeChats has the same updated avatar + name
    setActiveChats((prev) => {
      const exists = prev.some((c) => c._id === toUserId);
      if (!exists) {
        return [
          {
            _id: toUserId,
            profilename: target.profilename,
            picture: target.picture,
            lastMessage: "",
            lastMessageType: "text",
            unread: 0,
          },
          ...prev,
        ];
      }
      return prev.map((c) =>
        c._id === toUserId
          ? {
              ...c,
              profilename: target.profilename,
              picture: target.picture,
            }
          : c
      );
    });

    // reset pagination
    cursorRef.current = null;
    setHasMore(false);

    // now that E2EE is ready, fetch + decrypt
    await fetchMessages(toUserId, { initial: true, limit: 30 });
    await markMessagesAsRead(toUserId);
    setActiveChats((prev) =>
      prev.map((c) => (c._id === toUserId ? { ...c, unread: 0 } : c))
    );

    // land at bottom (ensure DOM ready)
    await nextPaint();
    const el = messagesContainerRef.current;
    if (el) hardJumpToBottom(el);
    stopStick();
  })();
}, [e2eeReady, toUserId, toUserName, toUserPicture]);

const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
  if (e.key === 'Enter' && !e.shiftKey && canSend) {
    e.preventDefault();
    handleSend();
  }
};


//Message menu
useEffect(() => {
  if (!msgMenuOpenId) return;
  const close = () => {
    setMsgMenuOpenId(null);
    setMsgMenuStyle(null);
  };
  window.addEventListener("scroll", close, true);
  window.addEventListener("resize", close);
  return () => {
    window.removeEventListener("scroll", close, true);
    window.removeEventListener("resize", close);
  };
}, [msgMenuOpenId]);

//close menu 
useEffect(() => {
  const onDocClick = (e: MouseEvent) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    if (t.closest('[data-role="msg-kebab"]')) return;
    if (t.closest('[data-role="msg-menu"]')) return;
    setMsgMenuOpenId(null);
  };
  document.addEventListener("click", onDocClick);
  return () => document.removeEventListener("click", onDocClick);
}, []);

  /** Fetch user suggestions based on search */
  useEffect(() => {
    const fetchSuggestions = async () => {
      const trimmedSearch = search.trim();

      if (!trimmedSearch) {
        setSuggestions([]);
        return;
      }
      try {
        const res = await api.get("/users/search", {
          params: { query: trimmedSearch },
        });

        const filteredUsers = res.data.filter((u: User) => u._id !== user?._id);
        setSuggestions(filteredUsers);
      } catch (err) {
        console.error("Error fetching suggestions:", err);
      }
    };
    fetchSuggestions();
  }, [search, user]);

// Track generated blob: URLs so we can revoke them
const myIdRef = useRef<string>("");
const canType = !!selectedUser;
const canSend = !!selectedUser && e2eeReady;

useEffect(() => {
  myIdRef.current = idOf(user);
}, [user]);

  // handleUserSelect
  const handleUserSelect = async (userArg: User) => {
    actionRef.current = "open";
  
    // 🔄 start from the clicked user, then let the backend override
    let target: User = {
      _id: userArg._id,
      profilename: userArg.profilename || "User",
      picture: userArg.picture || "",
    };
  
    // hydrate from backend so block rules apply
    try {
      const res = await api.get(`/users/${userArg._id}`);
      const data = res.data || {};
      target = {
        _id: userArg._id,
        profilename: data.profilename || userArg.profilename || "User",
        // ⬅️ IMPORTANT: use backend picture *as-is* (can be '')
        picture: typeof data.picture === "string" ? data.picture : "",
      };
    } catch (e) {
      console.error("Failed to hydrate selected user", e);
      // fall back to whatever we had (userArg)
    }
    setSelectedUser(target);
    // 🔁 reset pagination state
    cursorRef.current = null;
    setHasMore(false);
    // clear old messages
    setMessages([]);
    userAtBottomRef.current = true;
    setSearch("");
    setSuggestions([]);
    await markMessagesAsRead(target._id);
    setActiveChats(prev =>
      prev.map(c => (c._id === target._id ? { ...c, unread: 0 } : c))
    );
  
    // first page
    await fetchMessages(target._id, { initial: true, limit: 30 });
  
    // keep view pinned
    await nextPaint();
    snapNow();
    setTimeout(() => snapNow(), 0);
    setTimeout(() => snapNow(), 120);
  
    // make sure activeChats has same sanitized picture
    if (!activeChats.some(c => c._id === target._id)) {
      setActiveChats(prev => [
        {
          _id: target._id,
          profilename: target.profilename,
          picture: target.picture,
          lastMessage: "",
          lastMessageType: "text",
          unread: 0,
        },
        ...prev,
      ]);
    } else {
      setActiveChats(prev =>
        prev.map(c =>
          c._id === target._id
            ? { ...c, profilename: target.profilename, picture: target.picture }
            : c
        )
      );
    }
  };
  
  useEffect(() => {
    try {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch {}
  }, []);

// C) After the messages list changes (new items / open / send)
useLayoutEffect(() => {
  const el = messagesContainerRef.current;
  if (!el) return;
  const prevLen = prevLenRef.current ?? 0;
  const currLen = messages.length;
  const increased = currLen > prevLen;

  const shouldStick =
    !suppressStickRef.current && (
      actionRef.current === "open" ||
      actionRef.current === "send" ||
      (increased && userAtBottomRef.current)
    );

  if (shouldStick) {
    hardJumpToBottom(el);
  }
  prevLenRef.current = currLen;
  actionRef.current = null;
}, [messages.length, selectedUser?._id]);


useEffect(() => {
  const candidates = activeChats.filter(c => {
    const lm = (c.lastMessage || "").trim();
    return lm === "Message" || lm.length === 0;
  });

  if (!candidates.length) return;

  let cancelled = false;

  (async () => {
    for (const row of candidates) {
      try {
        // fetch just the latest message for this chat
        const res = await api.get(`/message/${row._id}`, {
          params: { limit: 1 },
        });

        const items = res.data?.items;
        if (!Array.isArray(items) || !items[0]) continue;

        const msg = normalizeMessage(items[0]);

        // decrypt if needed
        let text =
          msg.decryptedText ??
          msg.text ??
          "";

        if (msg.isEncrypted && !text && e2eeReady) {
          const me = myIdRef.current || idOf(user);
          const isMine = idOf(msg.sender) === me;
          const env = isMine ? msg.selfEnvelope : msg.encrypted;

          if (env) {
            const secret = await requireMySecret();
            text = await decryptMessage(env, secret);
          }
        }

        const finalText = text?.trim();
        if (!finalText) continue;

        if (cancelled) return;

        setActiveChats(prev =>
          prev.map(c =>
            c._id === row._id
              ? {
                  ...c,
                  lastMessage: finalText,
                  lastMessageType: "text",
                }
              : c
          )
        );
      } catch (e) {
        // silently ignore — preview can stay generic
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [activeChats, e2eeReady, user, setActiveChats]);

// If media selection came back empty, mark those bubbles as expired (no deletion)
useEffect(() => {
  const st = (location.state || {}) as any;
  const ex = st.expired as { parentId?: string; ids?: string[] } | undefined;
  if (!ex) return;

  // remember it for future loads
  expiredRef.current = {
    parentId: ex.parentId ? String(ex.parentId) : undefined,
    ids: (ex.ids || []).map(String),
  };

  // mark what we already have in memory
  setMessages(prev => applyExpired(prev));

  // scrub the flag so it doesn't reapply on back/forward
  navigate(location.pathname, { replace: true, state: { ...st, expired: undefined } });
}, [location.state, location.pathname, navigate, applyExpired]);

  return (
    <div style={pageUi.appShell}>
      <E2EEDebug />
      <div style={pageUi.sidebarFixed}>
  <SidebarChat
    search={search}
    onSearchChange={setSearch}
    suggestions={suggestions}
    onSelectSuggestion={handleUserSelect}
    activeChats={activeChats}
    selectedUserId={selectedUser?._id || null}
    onSelectChat={handleUserSelect}
    onDeleteChat={handleDeleteChat}
  />
</div>

{/* Left bottom terms privacy help buttons */}
<div
  style={{
    position: "fixed",
    left: 18,
    bottom: 8,
    width: 230,
    zIndex: 50,
    fontSize: 10,
    color: "#000",
    lineHeight: 1.6,
    textAlign: "left",
    pointerEvents: "auto",
  }}
>
  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/helpsupportstart")}
  >
    {t("mainSidebar.footer.helpSupport")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/privacypolicy")}
  >
    {t("mainSidebar.footer.privacyPolicy")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/termsofuse")}
  >
    {t("mainSidebar.footer.termsOfUse")}
  </span>
</div>
  
     {/* Right: Chat */}
<section style={pageUi.chatPane}>
  {selectedUser ? (
    <>
     {/* Header */}
<header
  style={{
    ...pageUi.chatHeader,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  }}
>
  {/* left: avatar + name */}
  <div style={{ display: "flex", alignItems: "center" }}>
    <div
      onClick={() => selectedUser && navigate(`/visitor-profile/${selectedUser._id}`)}
      style={{
        width: 42,
        height: 42,
        borderRadius: "10%",
        marginRight: 10,
        marginTop: -5,
        backgroundColor: selectedUser?.picture ? "transparent" : "rgb(38, 29, 162)",
        backgroundImage: selectedUser?.picture ? `url(${picUrl(selectedUser.picture)})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        transform: "scale(1.1)",          // try 1.15–1.4
        transformOrigin: "left center",
        alignItems: "center",
        justifyContent: "center",
        fontSize: selectedUser?.picture ? 0 : "30px",
        cursor: "pointer",
      }}
      role="button"
      tabIndex={0}
      aria-label={t('chat.openVisitorProfile')}
        title={selectedUser?.profilename || t('chat.fallbackUser')}
    >
      {!selectedUser?.picture && "👻"}
    </div>

    <span
      onClick={() => selectedUser && navigate(`/visitor-profile/${selectedUser._id}`)}
      style={{ cursor: "pointer", marginLeft: 10, fontSize: "1.1rem", color: "#000", fontWeight: 700, }}
      title={t('chat.openVisitorProfile')}    

    >
{selectedUser?.profilename || t('chat.unknownUser')}
</span>
  </div>

  {/* right: actions */}
  <div style={{ marginLeft: "auto" }}>
    <ProfileActions
      target={selectedUser} size={34}
      onBlocked={(uid) => {
        if (selectedUser && selectedUser._id === uid) {
          setSelectedUser(null);
          setMessages([]);
        }
      }}
      onRequestReport={(uid) => {
        navigate(`/report?targetType=user&targetId=${encodeURIComponent(uid)}`);
      }}
    />
  </div>
</header>


{/* Messages */}
<div
  ref={messagesContainerRef}   style={{ ...pageUi.messages, overflowAnchor: 'none' as any }}
   >  <div style={pageUi.messagesStack}>
    {/* Spacer pushes content to the bottom when list is short; harmless when long */}
    <div ref={topSentinelRef} style={{ height: 1 }} />
    {displayMessages.length > 0 ? (
  displayMessages.map((msg) => {
    const mine = isMine(msg);
    return (
      <MessageOutlook
        key={msg._id}
        msg={msg}
        mine={mine}
        hover={hoverMsgId === msg._id}
        onHoverChange={(hovering) =>
          setHoverMsgId((prev) =>
            hovering ? msg._id : prev === msg._id ? null : prev
          )
        }
        onCopy={handleCopyMessage}
        onReply={handleReplyToMessage}
        onDelete={handleDeleteMessage}
        findMessageById={findMessageById}
        ui={messageUi}
        msgMenuOpenId={msgMenuOpenId}
        msgMenuStyle={msgMenuStyle}
        setMsgMenuOpenId={setMsgMenuOpenId}
        setMsgMenuStyle={setMsgMenuStyle}
        computeAutoPosition={computeAutoPosition}
        showMediaFor={showMediaForMessage}   // ⬅️ new prop
        replyLookupTick={replyLookupTick}      />
    );
  })
) : (
  <p style={{ color: "#64748b", textAlign: "center", margin: 0 }}>...</p>
)}
  </div>
</div>

  {/* Composer */}
<div style={pageUi.composer}>
  {/* Media button */}
  <button
    type="button"
    title={t('chat.addMedia')}
    style={{
      ...pageUi.iconBtn,
      opacity: canType ? 1 : 0.5,
      pointerEvents: canType ? 'auto' : 'none',
      marginRight: 3,
    }}
    onClick={() => {
      if (!selectedUser) { alert(t('chat.pickChatFirst')); return; }
      navigate("/chat-upload-media", { state: { toUser: selectedUser } });
    }}
  >
    🖼️
  </button>

  {/* Textarea + emoji, with reply overlay inside */}
  <div
    style={{
      ...pageUi.fieldShell,
      // add a bit of room for the overlay ONLY when replying
      paddingTop: replyTo ? 30 : (pageUi.fieldShell as any).paddingTop || "6px",
    }}
  >
    {activeReply && (
  <div style={pageUi.replyBar}>
    <span style={pageUi.replyLabel}>{t('chat.replying')}</span>
    <span style={pageUi.replyText}>“{activeReply.text}”</span>
    <button
      type="button"
      aria-label="Cancel reply"
      style={pageUi.replyClose}
      onClick={() => setReplyTo(null)}
    >
      ×
    </button>
  </div>
)}

    <textarea
      ref={(el) => {
        textareaRef.current = el;
        inputRef.current = el; // ✅ make emoji/focus use the real textarea
      }}
      disabled={!canType}
      value={message}
      onChange={(e) => {
        setMessage(e.target.value);
        // wait for value to render then measure
        requestAnimationFrame(autoGrow);
      }}
      rows={1}
      onKeyDown={handleTextareaKeyDown}
      onInput={() => requestAnimationFrame(autoGrow)}
      style={pageUi.fieldTextarea}
    />
    <button
      ref={emojiBtnRef}
      type="button"
      title={t('chat.emoji')}
      disabled={!canType}
      style={{ ...pageUi.fieldEmojiBtn, opacity: canType ? 1 : 0.5, cursor: canType ? 'pointer' : 'not-allowed' }}
      onClick={() => canType && setEmojiOpen(v => !v)}
    >
      😊
    </button>
  </div>

  {/* Send button stays the same */}
  <button
    onClick={handleSend}
    disabled={!canSend || !message.trim()}
    style={{
      ...pageUi.sendBtn,
      opacity: (!canSend || !message.trim()) ? 0.5 : 1,
      cursor: (!canSend || !message.trim()) ? 'not-allowed' : 'pointer'
    }}
  >
      {t('chat.send')}

  </button>

  {/* Emoji picker unchanged */}
  <EmojiPickerChat
    visible={emojiOpen}
    onSelect={(emoji: any) => {
      const sym = emoji.native || (emoji?.unified ? emoji.native : "");
      const ta = inputRef.current;
      if (!ta || !sym) {
        setMessage((prev) => prev + (sym || ""));
        return;
      }
      const start = ta.selectionStart ?? ta.value.length;
      const end = ta.selectionEnd ?? ta.value.length;
      const next = ta.value.slice(0, start) + sym + ta.value.slice(end);
      setMessage(next);
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + sym.length;
        ta.setSelectionRange(pos, pos);
      });
    }}
    onClose={() => setEmojiOpen(false)}
    anchorRef={emojiBtnRef}
    placement="top-left-of"
    offset={8}
    theme="dark"
    closeOnEsc
    closeOnScrollOrResize
    closeOnSelect={false}
  />
</div>

    </>
  ) : (
    <p style={{ color: "#6b7280", margin: 20, marginTop: "4%", marginLeft: "4%" }}>
        {t('chat.selectUserHint')}
    </p>
  )}

</section>
{deleteTarget && (
  <div style={confirmOverlay}>
    <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
      <div style={{ color: "#000", marginBottom: 12, fontSize: 15 }}>
        {deleteTarget.mine
          ? t('chat.deleteMessage.everyone')
          : t('chat.deleteMessage.me')}
      </div>

      <button
        onClick={confirmDeleteMessage}
        disabled={deleteBusy}
        style={primaryBtn}
      >
        {deleteBusy ? t('common.deleting') : t('common.confirm')}
      </button>

      <button
        onClick={() => setDeleteTarget(null)}
        disabled={deleteBusy}
        style={cancelBtn}
      >
        {t('common.cancel')}
      </button>
    </div>
  </div>
)}

    </div>
  )
};

export default Chat;

const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.85)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 99999999,
};

const confirmCard: React.CSSProperties = {
  width: "92%",
  maxWidth: 420,
  background: "#0f172a",
  borderRadius: 16,
  padding: 20,
  textAlign: "center",
  border: "1px solid #334155",
  color: "#e5e7eb",
};

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 10,
  border: "none",
  background: "#b91c1c",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
  cursor: "pointer",
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 10,
  border: "none",
  background: "transparent",
  color: "#94a3b8",
  cursor: "pointer",
};
